import { useEffect, useMemo, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import { api } from '../../../shared/services/API'
import { getBackendError } from '../../../shared/utils/formErrors'
import type { Page } from '../../../shared/utils/types'
import type { TenantAdminListItem } from '../tenantModel'
import {
    CICLO_LABEL,
    ESTADO_SUBSCRICAO_LABEL,
    formatMoney,
    type CicloFaturacao,
    type EstadoSubscricao,
    type Plano,
    type Subscricao,
} from '../subscricoesModel'

interface Props {
    open: boolean
    initial: Subscricao | null
    /**
     * Cria a subscrição para esta empresa, sem o selector de empresa (usado no
     * painel da empresa em TenantDetail). Ignorado em edição (`initial` != null).
     */
    lockedTenant?: { id: string; nome: string } | null
    onClose: () => void
    onSaved: () => void
}

const CICLOS: CicloFaturacao[] = ['MENSAL', 'ANUAL']
const ESTADOS: EstadoSubscricao[] = ['ACTIVA', 'SUSPENSA', 'CANCELADA']

export default function SubscricaoFormDialog({ open, initial, lockedTenant, onClose, onSaved }: Props) {
    const editing = initial !== null
    const tenantFixo = !editing && lockedTenant != null

    const [planos, setPlanos] = useState<Plano[]>([])
    const [tenants, setTenants] = useState<TenantAdminListItem[]>([])

    const [tenantId, setTenantId] = useState<string>('')
    const [planoId, setPlanoId] = useState<string>('')
    const [ciclo, setCiclo] = useState<CicloFaturacao>('MENSAL')
    const [estado, setEstado] = useState<EstadoSubscricao>('ACTIVA')
    const [inicioEm, setInicioEm] = useState<string>('')
    const [proximaFaturacaoEm, setProximaFaturacaoEm] = useState<string>('')

    const [apiError, setApiError] = useState('')
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => {
        if (!open) return
        // eslint-disable-next-line react-hooks/set-state-in-effect -- reset dos campos e carregamento das listas quando o diálogo abre
        setApiError('')
        setTenantId(initial?.tenantId ?? lockedTenant?.id ?? '')
        setPlanoId(initial?.planoId ?? '')
        setCiclo(initial?.cicloFaturacao ?? 'MENSAL')
        setEstado(initial?.estado ?? 'ACTIVA')
        setInicioEm('')
        setProximaFaturacaoEm(initial?.proximaFaturacaoEm ?? '')

        api.get<Page<Plano>>('/api/admin/planos', { params: { size: 200, sort: 'nome,asc' } })
            .then((r) => setPlanos(r.data.content ?? []))
            .catch(() => setApiError('Não foi possível carregar os planos.'))

        if (!initial && !lockedTenant) {
            api.get<Page<TenantAdminListItem>>('/api/admin/tenants', { params: { size: 200, sort: 'nome,asc' } })
                .then((r) => setTenants(r.data.content ?? []))
                .catch(() => setApiError('Não foi possível carregar as empresas.'))
        }
    }, [open, initial, lockedTenant])

    const planoSel = useMemo(() => planos.find((p) => p.id === planoId) ?? null, [planos, planoId])
    const tenantSel = useMemo(() => tenants.find((t) => t.id === tenantId) ?? null, [tenants, tenantId])

    const submit = async () => {
        if (!editing && !tenantId) { setApiError('Escolha a empresa.'); return }
        if (!planoId) { setApiError('Escolha o plano.'); return }

        setSubmitting(true)
        setApiError('')
        try {
            if (editing) {
                // O preço acompanha sempre o plano (o backend usa `plano.preco`
                // quando não é enviado) — não é editável na subscrição.
                await api.patch(`/api/admin/subscricoes/${initial!.id}`, {
                    planoId,
                    cicloFaturacao: ciclo,
                    estado,
                    proximaFaturacaoEm: proximaFaturacaoEm || null,
                })
            } else {
                await api.post('/api/admin/subscricoes', {
                    tenantId,
                    planoId,
                    cicloFaturacao: ciclo,
                    inicioEm: inicioEm || null,
                })
            }
            onSaved()
        } catch (err) {
            setApiError(getBackendError(err)?.message ?? 'Não foi possível guardar a subscrição.')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 700 }}>{editing ? 'Editar subscrição' : 'Nova subscrição'}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {apiError && <Alert severity="error" sx={{ borderRadius: 2 }}>{apiError}</Alert>}

                {editing ? (
                    <TextField label="Empresa" value={initial?.tenantNome ?? initial?.tenantId ?? ''} disabled fullWidth />
                ) : tenantFixo ? (
                    <TextField label="Empresa" value={lockedTenant!.nome} disabled fullWidth />
                ) : (
                    <Autocomplete
                        options={tenants}
                        value={tenantSel}
                        onChange={(_, v) => setTenantId(v?.id ?? '')}
                        getOptionLabel={(t) => t.nome}
                        renderInput={(params) => <TextField {...params} label="Empresa" />}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                    />
                )}

                <TextField
                    select
                    label="Plano"
                    value={planoId}
                    onChange={(e) => setPlanoId(e.target.value)}
                    fullWidth
                    helperText={
                        planoSel && !planoSel.cobravel
                            ? 'Plano demo — a subscrição não gera cobranças.'
                            : !editing && planoSel
                                ? `Preço da subscrição: ${formatMoney(planoSel.preco)} — definido pelo plano`
                                : undefined
                    }
                >
                    {planos.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                            {p.nome} — {formatMoney(p.preco)} · {p.propriedadesIlimitadas ? 'propriedades ilimitadas' : `${p.totalPropriedades} propriedade(s)`}
                        </MenuItem>
                    ))}
                </TextField>

                {editing ? (
                    <TextField
                        select
                        label="Ciclo de faturação"
                        value={ciclo}
                        onChange={(e) => setCiclo(e.target.value as CicloFaturacao)}
                        fullWidth
                    >
                        {CICLOS.map((c) => <MenuItem key={c} value={c}>{CICLO_LABEL[c]}</MenuItem>)}
                    </TextField>
                ) : (
                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <TextField select label="Ciclo de faturação" value={ciclo} onChange={(e) => setCiclo(e.target.value as CicloFaturacao)} sx={{ flex: 1 }}>
                            {CICLOS.map((c) => <MenuItem key={c} value={c}>{CICLO_LABEL[c]}</MenuItem>)}
                        </TextField>
                        <TextField
                            label="Início"
                            type="date"
                            value={inicioEm}
                            onChange={(e) => setInicioEm(e.target.value)}
                            helperText="Vazio = hoje. A 1.ª cobrança conta a partir daqui."
                            slotProps={{ inputLabel: { shrink: true } }}
                            sx={{ flex: 1 }}
                        />
                    </Box>
                )}

                {editing && (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField select label="Estado" value={estado} onChange={(e) => setEstado(e.target.value as EstadoSubscricao)} sx={{ flex: 1 }}>
                            {ESTADOS.map((s) => <MenuItem key={s} value={s}>{ESTADO_SUBSCRICAO_LABEL[s]}</MenuItem>)}
                        </TextField>
                        <TextField
                            label="Próxima faturação"
                            type="date"
                            value={proximaFaturacaoEm ?? ''}
                            onChange={(e) => setProximaFaturacaoEm(e.target.value)}
                            disabled={estado !== 'ACTIVA'}
                            slotProps={{ inputLabel: { shrink: true } }}
                            sx={{ flex: 1 }}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={submitting}>Cancelar</Button>
                <Button variant="contained" onClick={submit} disabled={submitting}>
                    {submitting ? 'A guardar…' : 'Guardar'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
